// All 24 quiz questions with scoring across career zones and traits.
// Each question option scores into one or more zones (z) and/or traits (t).

export const ZONES = {
  tech: {
    id: 'tech',
    name: 'Tech & AI Builder',
    color: '#0055FF',
    short: 'You like building with code, AI, and systems.',
    avatar: {
      name: 'The AI Builder',
      line: 'You like solving problems with systems, tools, and technology.',
      image: '/avatars/tech.png',
    },
    careers: ['AI Engineer', 'Product Manager', 'Software Developer', 'Robotics Engineer'],
    skills: ['Python basics', 'AI tools (ChatGPT, Claude)', 'No-code app builders', 'Git & GitHub', 'Logic & problem solving'],
    project: 'Build a simple AI chatbot or a no-code tool that solves a problem at your school.',
    action: 'Start a 30-day Python or AI tools challenge — one mini build a week.',
  },
  business: {
    id: 'business',
    name: 'Business & Startup Thinker',
    color: '#FF5A00',
    short: 'You love ideas, brands, growth and entrepreneurship.',
    avatar: {
      name: 'The Startup Strategist',
      line: 'You like ideas, leadership, growth, and building things people want.',
      image: '/avatars/business.png',
    },
    careers: ['Entrepreneur', 'Marketing Lead', 'Product Strategist', 'Brand Manager'],
    skills: ['Business model thinking', 'Storytelling & pitching', 'Brand & content basics', 'Money & pricing', 'Sales psychology'],
    project: 'Launch a tiny side-hustle: design a product/brand and try to get your first 3 paying users.',
    action: 'Read 1 founder story a week and write a 1-page “what I learned” note.',
  },
  creative: {
    id: 'creative',
    name: 'Creative & Design Maker',
    color: '#9D4CDD',
    short: 'You think in visuals, stories and aesthetics.',
    avatar: {
      name: 'The Creative Maker',
      line: 'You like turning ideas into stories, visuals, content, and experiences.',
      image: '/avatars/creative.png',
    },
    careers: ['UI/UX Designer', 'Content Creator', 'Filmmaker', 'Brand Designer'],
    skills: ['Figma / Canva', 'Visual storytelling', 'Short-form video', 'Typography & color', 'Personal brand'],
    project: 'Create a 7-post mini series on Instagram or YouTube Shorts around a topic you love.',
    action: 'Publish one piece of content every week for 30 days.',
  },
  people: {
    id: 'people',
    name: 'People & Psychology Explorer',
    color: '#00D95F',
    short: 'You love understanding people, emotions and behaviour.',
    avatar: {
      name: 'The Human Decoder',
      line: 'You like understanding people, emotions, behaviour, and communication.',
      image: '/avatars/people.png',
    },
    careers: ['Psychologist', 'UX Researcher', 'Teacher', 'HR / People Lead'],
    skills: ['Active listening', 'Interview & research', 'Behavioural psychology basics', 'Communication', 'Empathy mapping'],
    project: 'Interview 10 students on a real problem (stress, focus, friendships) and publish your top 5 insights.',
    action: 'Read one short psychology article each week and note one insight you can apply.',
  },
  science: {
    id: 'science',
    name: 'Science & Research Investigator',
    color: '#0E7490',
    short: 'You love experiments, biology, physics and discovery.',
    avatar: {
      name: 'The Deep Investigator',
      line: 'You like asking big questions, researching deeply, and discovering how things work.',
      image: '/avatars/science.png',
    },
    careers: ['Researcher', 'Doctor', 'Biotech Scientist', 'Data Scientist'],
    skills: ['Research methods', 'Critical thinking', 'Data literacy', 'Reading scientific papers', 'Lab basics'],
    project: 'Pick a question that excites you, run a mini research project and write a 2-page report.',
    action: 'Each month, study one Nobel-winning idea and explain it simply in your own words.',
  },
  finance: {
    id: 'finance',
    name: 'Finance & Economics Strategist',
    color: '#0F172A',
    short: 'You love numbers, money, markets and decisions.',
    avatar: {
      name: 'The Money Strategist',
      line: 'You like numbers, patterns, markets, decisions, and value.',
      image: '/avatars/finance.png',
    },
    careers: ['Investment Analyst', 'Economist', 'Founder/CFO', 'Consultant'],
    skills: ['Personal finance', 'Excel/Sheets', 'Market reading', 'Economic thinking', 'Data analysis'],
    project: 'Build a virtual ₹10,000 investment portfolio and track it weekly for 8 weeks.',
    action: 'Track one habit: read business news 10 mins a day, 5 days a week.',
  },
  law: {
    id: 'law',
    name: 'Law, Policy & Society Leader',
    color: '#FFD600',
    short: 'You love debate, justice, public speaking and big ideas.',
    avatar: {
      name: 'The Voice of Change',
      line: 'You like debate, fairness, leadership, public issues, and big ideas.',
      image: '/avatars/law.png',
    },
    careers: ['Lawyer', 'Policy Analyst', 'Journalist', 'Public Speaker'],
    skills: ['Public speaking', 'Argument & debate', 'Research & writing', 'Civic awareness', 'Negotiation'],
    project: 'Pick a policy issue you care about — write a 500-word op-ed and share it publicly.',
    action: 'Join one MUN, debate, or policy club in the next 2 months.',
  },
  impact: {
    id: 'impact',
    name: 'Impact & Sustainability Changemaker',
    color: '#16A34A',
    short: 'You want to solve social, environmental, real-world problems.',
    avatar: {
      name: 'The Impact Builder',
      line: 'You care about solving meaningful problems for people, society, and the planet.',
      image: '/avatars/impact.png',
    },
    careers: ['Social Entrepreneur', 'Sustainability Consultant', 'Policy Researcher', 'NGO Founder'],
    skills: ['Systems thinking', 'Community design', 'Storytelling for impact', 'Project execution', 'Sustainability basics'],
    project: 'Pick one local problem and run a 30-day micro-campaign with measurable impact.',
    action: 'Volunteer with one cause-driven org for at least 1 month.',
  },
};

export const TRAITS = {
  Analytical: 'You break problems down logically and notice patterns others miss.',
  Creative: 'You make new connections and turn ideas into something tangible.',
  'People-Oriented': 'You read people well and care about how they feel.',
  Builder: 'You love making things — from prototypes to projects to products.',
  Leader: 'You take initiative, set direction and bring others along.',
  Researcher: 'You go deep, ask “why”, and dig until you understand it fully.',
  Communicator: 'You can shape ideas into words and share them clearly.',
  'Problem Solver': 'You enjoy puzzles and finding the smartest path forward.',
  'Self-Driven': 'You move on your own steam — no one has to push you.',
  Strategic: 'You think in systems, trade-offs and second-order effects.',
};

export const LEVELS = [
  { id: 1, name: 'Know Yourself', range: [1, 6], message: "You've completed Level 1. You're starting to understand what makes you, you." },
  { id: 2, name: 'Decode Your Interests', range: [7, 12], message: "You've completed Level 2. Your career patterns are starting to appear." },
  { id: 3, name: 'Build Your Skill Stack', range: [13, 18], message: "You've completed Level 3. Your future skill stack is getting stronger." },
  { id: 4, name: 'Unlock Your Future Path', range: [19, 24], message: 'You completed the Career Fit Assessment. Your Future Profile is ready.' },
];

// Helper to make options
const opt = (id, text, icon, z = {}, t = {}) => ({ id, text, icon, z, t });

export const QUESTIONS = [
  { id: 1, q: 'When you get a school project, what part excites you most?', options: [
    opt('A', 'Finding a smart technical solution', 'Cpu', { tech: 2 }, { Analytical: 1, Builder: 1 }),
    opt('B', 'Coming up with a unique idea', 'Lightbulb', { creative: 2 }, { Creative: 1, 'Problem Solver': 1 }),
    opt('C', 'Understanding how it helps people', 'HeartHandshake', { people: 1, impact: 2 }, { 'People-Oriented': 1 }),
    opt('D', 'Presenting it confidently', 'Mic', { law: 1 }, { Communicator: 2, Leader: 1 }),
  ]},
  { id: 2, q: 'Which activity would you enjoy most?', options: [
    opt('A', 'Building an app, chatbot, or AI tool', 'Bot', { tech: 3 }, { Builder: 1 }),
    opt('B', 'Starting a small business or brand', 'Store', { business: 3 }, { Leader: 1 }),
    opt('C', 'Designing a poster, video, or campaign', 'Palette', { creative: 3 }, { Creative: 1 }),
    opt('D', 'Helping a friend solve a personal problem', 'Users', { people: 3 }, { 'People-Oriented': 1 }),
  ]},
  { id: 3, q: 'What kind of problems attract you most?', options: [
    opt('A', 'Technology problems', 'Wrench', { tech: 3 }, { 'Problem Solver': 1 }),
    opt('B', 'Business or money problems', 'TrendingUp', { business: 2, finance: 2 }, { Strategic: 1 }),
    opt('C', 'Human behaviour problems', 'Brain', { people: 3 }, { Researcher: 1 }),
    opt('D', 'Social or environmental problems', 'Leaf', { impact: 3, law: 1 }, {}),
  ]},
  { id: 4, q: 'Which subject area do you naturally enjoy more?', options: [
    opt('A', 'Maths / Computer Science', 'Calculator', { tech: 2, finance: 1 }, { Analytical: 1 }),
    opt('B', 'Business / Economics', 'BarChart3', { business: 2, finance: 2 }, { Strategic: 1 }),
    opt('C', 'Biology / Psychology', 'Microscope', { science: 2, people: 2 }, { Researcher: 1 }),
    opt('D', 'Art / English / Media', 'Pen', { creative: 2 }, { Communicator: 2, Creative: 1 }),
  ]},
  { id: 5, q: 'Your friends usually come to you for:', options: [
    opt('A', 'Logical advice', 'Compass', { science: 1, finance: 1 }, { Analytical: 2 }),
    opt('B', 'Creative ideas', 'Sparkles', { creative: 2 }, { Creative: 2 }),
    opt('C', 'Emotional support', 'Heart', { people: 2 }, { 'People-Oriented': 2 }),
    opt('D', 'Leadership or decision-making', 'Crown', { business: 1, law: 1 }, { Leader: 2 }),
  ]},
  { id: 6, q: 'Which video would you click first?', options: [
    opt('A', 'How AI is changing the world', 'Rocket', { tech: 3 }, { Analytical: 1 }),
    opt('B', 'How startups become successful', 'TrendingUp', { business: 3 }, { Strategic: 1 }),
    opt('C', 'Why people behave the way they do', 'Brain', { people: 3 }, { Researcher: 1 }),
    opt('D', 'How to fix India’s biggest problems', 'Globe2', { impact: 2, law: 2 }, {}),
  ]},
  { id: 7, q: 'What sounds most exciting?', options: [
    opt('A', 'Creating a chatbot', 'Bot', { tech: 3 }, { Builder: 1 }),
    opt('B', 'Building a brand', 'Store', { business: 2, creative: 1 }, { Strategic: 1 }),
    opt('C', 'Running a debate', 'Mic', { law: 3 }, { Communicator: 1 }),
    opt('D', 'Doing a science experiment', 'FlaskConical', { science: 3 }, { Researcher: 1 }),
  ]},
  { id: 8, q: 'Which role would you take in a group project?', options: [
    opt('A', 'Researcher', 'Search', { science: 2 }, { Researcher: 2 }),
    opt('B', 'Designer', 'Palette', { creative: 2 }, { Creative: 2 }),
    opt('C', 'Presenter', 'Mic', { law: 1 }, { Communicator: 2 }),
    opt('D', 'Organizer / leader', 'Crown', { business: 1 }, { Leader: 2, Strategic: 1 }),
  ]},
  { id: 9, q: 'What do you value most in a career?', options: [
    opt('A', 'Solving complex problems', 'Puzzle', { tech: 1, science: 1 }, { Analytical: 2 }),
    opt('B', 'Creating something of my own', 'Hammer', { business: 2, creative: 1 }, { Builder: 1 }),
    opt('C', 'Helping people', 'HeartHandshake', { people: 2, impact: 1 }, { 'People-Oriented': 1 }),
    opt('D', 'Making impact at scale', 'Globe2', { impact: 3 }, { Leader: 1 }),
  ]},
  { id: 10, q: 'Which skill would you like to build first?', options: [
    opt('A', 'Coding or AI tools', 'Code2', { tech: 3 }, { Builder: 1 }),
    opt('B', 'Public speaking', 'Mic', { law: 1 }, { Communicator: 2, Leader: 1 }),
    opt('C', 'Design or content creation', 'Palette', { creative: 3 }, { Creative: 1 }),
    opt('D', 'Financial thinking', 'PieChart', { finance: 3 }, { Strategic: 1 }),
  ]},
  { id: 11, q: 'If you had to create a project, you would choose:', options: [
    opt('A', 'AI solution for students', 'Bot', { tech: 2, impact: 1 }, { Builder: 1 }),
    opt('B', 'Mini startup idea', 'Rocket', { business: 3 }, { Strategic: 1 }),
    opt('C', 'Mental health awareness campaign', 'HeartPulse', { people: 2, creative: 1, impact: 1 }, {}),
    opt('D', 'Research report on climate or health', 'Leaf', { science: 2, impact: 2 }, { Researcher: 1 }),
  ]},
  { id: 12, q: 'What comes more naturally to you?', options: [
    opt('A', 'Numbers and patterns', 'BarChart3', { finance: 2, tech: 1 }, { Analytical: 2 }),
    opt('B', 'Stories and visuals', 'BookOpen', { creative: 2 }, { Creative: 1, Communicator: 1 }),
    opt('C', 'People and emotions', 'Heart', { people: 3 }, { 'People-Oriented': 1 }),
    opt('D', 'Rules and arguments', 'Scale', { law: 3 }, { Analytical: 1 }),
  ]},
  { id: 13, q: 'Which future career sounds most interesting?', options: [
    opt('A', 'AI Product Manager', 'Cpu', { tech: 2, business: 1 }, { Strategic: 1 }),
    opt('B', 'Entrepreneur', 'Rocket', { business: 3 }, { Leader: 1 }),
    opt('C', 'Psychologist', 'Brain', { people: 3 }, { Researcher: 1 }),
    opt('D', 'Policy Advisor', 'Landmark', { law: 2, impact: 1 }, { Strategic: 1 }),
  ]},
  { id: 14, q: 'You feel proud when you:', options: [
    opt('A', 'Solve a difficult question', 'Puzzle', { science: 1, tech: 1 }, { Analytical: 2 }),
    opt('B', 'Create something original', 'Sparkles', { creative: 2 }, { Creative: 2 }),
    opt('C', 'Help someone feel better', 'Heart', { people: 2 }, { 'People-Oriented': 2 }),
    opt('D', 'Influence a group decision', 'Megaphone', { law: 1, business: 1 }, { Leader: 2 }),
  ]},
  { id: 15, q: 'Which environment would you prefer?', options: [
    opt('A', 'Lab / innovation space', 'FlaskConical', { science: 2, tech: 1 }, { Researcher: 1 }),
    opt('B', 'Startup office', 'Rocket', { business: 3 }, { Builder: 1 }),
    opt('C', 'Design studio', 'Palette', { creative: 3 }, { Creative: 1 }),
    opt('D', 'Social impact organization', 'Globe2', { impact: 3 }, { 'People-Oriented': 1 }),
  ]},
  { id: 16, q: 'If you could intern somewhere, what would you choose?', options: [
    opt('A', 'Tech company', 'Cpu', { tech: 3 }, { Builder: 1 }),
    opt('B', 'Investment or business firm', 'TrendingUp', { finance: 2, business: 2 }, { Strategic: 1 }),
    opt('C', 'Hospital or research lab', 'Stethoscope', { science: 2, people: 1 }, { Researcher: 1 }),
    opt('D', 'Media, design, or content company', 'Clapperboard', { creative: 3 }, { Communicator: 1 }),
  ]},
  { id: 17, q: 'What type of challenge would you enjoy?', options: [
    opt('A', 'Building a working prototype', 'Hammer', { tech: 2 }, { Builder: 2 }),
    opt('B', 'Pitching an idea to judges', 'Mic', { business: 2 }, { Communicator: 1, Leader: 1 }),
    opt('C', 'Understanding why people make choices', 'Brain', { people: 2, finance: 1 }, { Researcher: 1 }),
    opt('D', 'Creating a campaign for a cause', 'Megaphone', { creative: 1, impact: 2 }, { Communicator: 1 }),
  ]},
  { id: 18, q: 'Which statement feels most like you?', options: [
    opt('A', 'I like figuring out how things work', 'Cog', { tech: 1, science: 2 }, { Analytical: 1 }),
    opt('B', 'I like making things look or sound better', 'Palette', { creative: 3 }, { Creative: 1 }),
    opt('C', 'I like understanding people', 'Users', { people: 3 }, { 'People-Oriented': 1 }),
    opt('D', 'I like leading change', 'Flag', { impact: 2, law: 1 }, { Leader: 1 }),
  ]},
  { id: 19, q: 'What would you rather read about?', options: [
    opt('A', 'Space, biology, or inventions', 'Telescope', { science: 3 }, { Researcher: 1 }),
    opt('B', 'Money, markets, or startups', 'TrendingUp', { finance: 2, business: 2 }, { Strategic: 1 }),
    opt('C', 'Human mind and relationships', 'Brain', { people: 3 }, { 'People-Oriented': 1 }),
    opt('D', 'Law, society, or current affairs', 'Newspaper', { law: 2, impact: 1 }, { Communicator: 1 }),
  ]},
  { id: 20, q: 'Which school event would you join?', options: [
    opt('A', 'Robotics / coding challenge', 'Bot', { tech: 3 }, { Builder: 1 }),
    opt('B', 'Business plan competition', 'Briefcase', { business: 2, finance: 1 }, { Strategic: 1 }),
    opt('C', 'Debate / MUN', 'Scale', { law: 3 }, { Communicator: 1 }),
    opt('D', 'Art / film / content festival', 'Clapperboard', { creative: 3 }, { Communicator: 1 }),
  ]},
  { id: 21, q: 'What kind of impact do you want to create?', options: [
    opt('A', 'Make life easier through technology', 'Cpu', { tech: 3 }, { 'Problem Solver': 1 }),
    opt('B', 'Build a successful company or product', 'Rocket', { business: 3 }, { Leader: 1 }),
    opt('C', 'Help people live better lives', 'Heart', { people: 2, impact: 1 }, { 'People-Oriented': 1 }),
    opt('D', 'Solve social or national problems', 'Globe2', { impact: 3, law: 1 }, {}),
  ]},
  { id: 22, q: 'Which task sounds most fun?', options: [
    opt('A', 'Analyzing data', 'BarChart3', { finance: 2, tech: 1 }, { Analytical: 1 }),
    opt('B', 'Creating a video', 'Clapperboard', { creative: 3 }, { Creative: 1 }),
    opt('C', 'Interviewing people', 'Mic', { people: 2 }, { Researcher: 1, Communicator: 1 }),
    opt('D', 'Preparing a speech', 'Megaphone', { law: 1 }, { Communicator: 2, Leader: 1 }),
  ]},
  { id: 23, q: 'When facing a new problem, you first:', options: [
    opt('A', 'Break it into steps', 'ListChecks', { tech: 1 }, { Analytical: 2, Strategic: 1 }),
    opt('B', 'Imagine different solutions', 'Lightbulb', { business: 1 }, { Creative: 2, 'Problem Solver': 1 }),
    opt('C', 'Ask how people are affected', 'Heart', { impact: 1 }, { 'People-Oriented': 2 }),
    opt('D', 'Think about the bigger system', 'Network', { law: 1, impact: 1 }, { Strategic: 2 }),
  ]},
  { id: 24, q: 'Your ideal project would be:', options: [
    opt('A', 'Useful and functional', 'Wrench', { tech: 2 }, { Builder: 2 }),
    opt('B', 'Creative and unique', 'Sparkles', { creative: 3 }, { Creative: 1 }),
    opt('C', 'Helpful and meaningful', 'HeartHandshake', { people: 1, impact: 2 }, { 'People-Oriented': 1 }),
    opt('D', 'Bold and influential', 'Megaphone', { law: 1, business: 1 }, { Leader: 2 }),
  ]},
];

export const CLARITY_LEVELS = [
  { value: 1, label: 'I am very confused' },
  { value: 2, label: 'I have some ideas but no clarity' },
  { value: 3, label: 'I have 2–3 options' },
  { value: 4, label: 'I am clear but want validation' },
];

export const SELF_ARCHETYPES = [
  { id: 'Explorer', label: 'Explorer', sub: 'I am curious but confused', icon: 'Compass' },
  { id: 'Builder',  label: 'Builder',  sub: 'I like creating things',      icon: 'Hammer' },
  { id: 'Thinker',  label: 'Thinker',  sub: 'I like analyzing deeply',     icon: 'Brain' },
  { id: 'Speaker',  label: 'Speaker',  sub: 'I like sharing ideas',        icon: 'Mic' },
  { id: 'Helper',   label: 'Helper',   sub: 'I like helping people',       icon: 'HeartHandshake' },
  { id: 'Leader',   label: 'Leader',   sub: 'I like taking charge',        icon: 'Crown' },
];


// Append a "None of the above" option to every question. Scoring is intentionally
// neutral (no zone / trait points) — we use this signal only to estimate how much
// extra career guidance the student likely needs.
QUESTIONS.forEach((q) => {
  q.options.push({
    id: 'E',
    text: 'None of the above',
    icon: 'HelpCircle',
    z: {},
    t: {},
    neutral: true,
  });
});
